import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';
import { ChevronLeft, ChevronRight, Images, Hotel } from 'lucide-react';

export const HotelPhoto = ({ images = [], name, className = '', onOpen, testId, cityFallback = false }) => (
  images.length ? (
    <button type="button" onClick={() => onOpen(0)} className={`relative group overflow-hidden rounded-lg bg-slate-100 ${className}`} data-testid={testId} aria-label={`View ${images.length} photos of ${name}`}>
      <img src={images[0]} alt={name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
      <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white">{cityFallback ? 'City view · hotel photos coming soon' : <><Images className="h-3 w-3" />{images.length} photo{images.length !== 1 ? 's' : ''}</>}</span>
    </button>
  ) : (
    <div className={`flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 ${className}`} data-testid={`${testId}-placeholder`}><Hotel className="h-6 w-6" /></div>
  )
);

export const HotelGallery = ({ images = [], name, index, onClose }) => {
  const open = index !== null && index !== undefined;
  const [i, setI] = useState(index || 0);
  useEffect(() => { if (open) setI(index); }, [index, open]);
  const step = (d) => setI((x) => (x + d + images.length) % images.length);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'ArrowRight') step(1); if (e.key === 'ArrowLeft') step(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, images.length]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !images.length) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[96vw] max-w-5xl p-0 gap-0 overflow-hidden bg-slate-950 border-slate-800 text-white [&>*]:min-w-0" data-testid="hotel-gallery">
        <DialogTitle className="sr-only">{name} photos</DialogTitle>
        <DialogDescription className="sr-only">Photo gallery for {name}</DialogDescription>
        <div className="relative w-full min-w-0 max-w-full bg-black overflow-hidden h-[60vh] sm:h-[70vh]">
          <img src={images[i]} alt={`${name} ${i + 1}`} className="block h-full w-full max-w-full object-contain" data-testid="hotel-gallery-main" />
          {images.length > 1 && (
            <>
              <button type="button" onClick={() => step(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 hover:bg-white" data-testid="hotel-gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" onClick={() => step(1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 hover:bg-white" data-testid="hotel-gallery-next"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
          <div className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold" data-testid="hotel-gallery-counter">{i + 1} / {images.length}</div>
        </div>
        <div className="p-3 space-y-2">
          <div className="font-bold text-sm">{name}</div>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="hotel-gallery-thumbs">
            {images.map((src, k) => (
              <button key={k} type="button" onClick={() => setI(k)} className={`shrink-0 h-16 w-24 overflow-hidden rounded-md border-2 ${k === i ? 'border-[#FF6600]' : 'border-transparent opacity-70 hover:opacity-100'}`} data-testid={`hotel-gallery-thumb-${k}`}>
                <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
